// Merch store, admin Orders screen: the ledger, required-item tracking, the
// picking list and packing slips for one sale. Split from admin-store.js,
// which keeps the catalog and the parent-view preview; the "Preview" button
// on this screen opens that preview.
//
// Wired via VIEW_INIT['a-store-orders'] + wireStoreOrders(), also called from
// soLoad()'s and soSelectTab()'s direct #main rewrites.
import { apiFetch } from '../core/api.js';
import { STORE_SALES, currentView } from '../core/state.js';
import { fmtLocalDT } from '../core/format.js';
import { toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, viewParent } from '../core/routing.js';
import { REQUIRED_CATEGORY_LABELS, previewSale } from './admin-store.js';

// ── MERCH STORE: admin orders (ledger / required-item tracking / fulfillment / packing slips) ──
let soSaleId = null;
let soTab = 'orders'; // 'orders' | 'tracking' | 'picking' | 'packing'
let soPickingList = null;
let soTracking = null;
let soOrders = null; // backs both the Orders ledger and Packing Slips — same underlying data
let soLoadingKey = null; // guards against the same re-entrancy issue as wrSelectProgram

// Packing Slips has no state of its own — it's just soOrders regrouped by family,
// so it rides on the same fetch as the Orders tab instead of hitting the API again.
function soFetchTabFor(tab) { return tab === 'packing' ? 'orders' : tab; }
function soDataLoadedFor(tab) {
  const t = soFetchTabFor(tab);
  if (t === 'picking') return soPickingList !== null;
  if (t === 'tracking') return soTracking !== null;
  return soOrders !== null;
}

async function soLoad(saleId, tab) {
  soSaleId = saleId;
  soTab = tab;
  const fetchTab = soFetchTabFor(tab);
  const key = saleId + ':' + fetchTab;
  soLoadingKey = key;
  if (currentView === 'a-store-orders') { document.getElementById('main').innerHTML = VIEWS['a-store-orders'](); wireStoreOrders(); }
  try {
    let data;
    if (fetchTab === 'picking') data = await apiFetch('/store/sales/' + saleId + '/picking-list');
    else if (fetchTab === 'tracking') data = await apiFetch('/store/sales/' + saleId + '/required-tracking');
    else data = await apiFetch('/store/sales/' + saleId + '/orders');
    if (soLoadingKey !== key) return;
    if (fetchTab === 'picking') soPickingList = data;
    else if (fetchTab === 'tracking') soTracking = data;
    else soOrders = data;
  } catch (e) {
    if (soLoadingKey !== key) return;
    if (fetchTab === 'picking') soPickingList = [];
    else if (fetchTab === 'tracking') soTracking = { categories: [], performers: [] };
    else soOrders = [];
    toastError(e.message);
  }
  soLoadingKey = null;
  if (currentView === 'a-store-orders') { document.getElementById('main').innerHTML = VIEWS['a-store-orders'](); wireStoreOrders(); }
}
function soSelectSale(saleId) {
  soPickingList = null; soTracking = null; soOrders = null;
  soLoad(saleId, soTab);
}
function soSelectTab(tab) {
  if (!soDataLoadedFor(tab)) { soLoad(soSaleId, tab); return; }
  soTab = tab;
  if (currentView === 'a-store-orders') { document.getElementById('main').innerHTML = VIEWS['a-store-orders'](); wireStoreOrders(); }
}
// Wires the sale-tabs row, the Orders/Tracking/Picking/Packing tab buttons,
// and whichever tab-specific action button is currently rendered — called
// from VIEW_INIT['a-store-orders'] and from soLoad()'s/soSelectTab()'s direct
// #main rewrites, both of which bypass VIEW_INIT.
function wireStoreOrders() {
  document.querySelectorAll('[data-action="so-select-sale"]').forEach(btn => {
    btn.addEventListener('click', () => soSelectSale(btn.dataset.sale));
  });
  document.querySelectorAll('[data-action="so-select-tab"]').forEach(btn => {
    btn.addEventListener('click', () => soSelectTab(btn.dataset.tab));
  });
  document.querySelectorAll('[data-action="so-preview-sale"]').forEach(btn => {
    btn.addEventListener('click', () => previewSale(btn.dataset.sale));
  });
  document.querySelectorAll('[data-action="so-copy-fulfillment"]').forEach(btn => {
    btn.addEventListener('click', () => copyFulfillmentText(btn.dataset.saleName));
  });
  document.querySelectorAll('[data-action="so-print-packing"]').forEach(btn => {
    btn.addEventListener('click', () => printPackingSlips(btn.dataset.sale));
  });
}
// Groups a sale's orders by family, merging line items across that family's
// separate checkouts (e.g. the shirt bought in September, tights in November)
// into one consolidated list — Lori is bagging up everything a family gets,
// not reproducing individual transactions.
function soPackingSlips() {
  const byAccount = {};
  (soOrders || []).forEach(o => {
    if (!byAccount[o.accountId]) byAccount[o.accountId] = { parentName: o.parentName, total: 0, lines: {} };
    byAccount[o.accountId].total += o.totalAmount;
    o.items.forEach(i => {
      const recipient = i.recipient || '—';
      const key = i.productName + '|' + i.size + '|' + recipient;
      if (!byAccount[o.accountId].lines[key]) byAccount[o.accountId].lines[key] = { productName: i.productName, size: i.size, recipient, quantity: 0 };
      byAccount[o.accountId].lines[key].quantity += i.quantity;
    });
  });
  return Object.values(byAccount)
    .map(f => ({ ...f, lines: Object.values(f.lines).sort((a, b) => a.recipient.localeCompare(b.recipient) || a.productName.localeCompare(b.productName)) }))
    .sort((a, b) => a.parentName.localeCompare(b.parentName));
}
function copyFulfillmentText(saleName) {
  if (!soPickingList || !soPickingList.length) { toast('Nothing to copy yet'); return; }
  const text = `${saleName} — Fulfillment\n` + soPickingList.map(r => `${r.productName} (${r.size}) x ${r.totalQty}`).join('\n');
  navigator.clipboard.writeText(text).then(() => toast('Copied to clipboard')).catch(() => toast('Could not copy — select the list and copy manually'));
}
// Opens a clean, light-themed printable document — same pattern as
// downloadReceipt() — rather than trying to print the dark admin UI itself.
function printPackingSlips(saleId) {
  const sale = STORE_SALES.find(x => x.id === saleId);
  const families = soPackingSlips();
  if (!sale || !families.length) { toast('No orders to print yet'); return; }
  const slipsHtml = families.map(f => `
    <div class="slip">
      <div class="slip-family">${esc(f.parentName)}</div>
      <div class="slip-sale">${esc(sale.name)}</div>
      <table>
        <thead><tr><th></th><th>Item</th><th>Size</th><th>For</th><th style="text-align:right">Qty</th></tr></thead>
        <tbody>${f.lines.map(l => `<tr><td class="check">&#9633;</td><td>${esc(l.productName)}</td><td>${esc(l.size)}</td><td>${esc(l.recipient)}</td><td style="text-align:right">${l.quantity}</td></tr>`).join('')}</tbody>
      </table>
      <div class="slip-total">Total paid: ${fmt$(f.total)}</div>
    </div>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com;">
  <title>Packing Slips — ${esc(sale.name)}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Source Sans 3', sans-serif; font-weight: 300; color: #1a1a2e; background: #fff; }
    .slip { padding: 40px; page-break-after: always; max-width: 640px; margin: 0 auto; }
    .slip:last-child { page-break-after: auto; }
    .slip-family { font-family: 'Libre Baskerville', serif; font-size: 24px; font-weight: 700; margin-bottom: 2px; }
    .slip-sale { font-size: 12px; color: #9e9488; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #9e9488; padding-bottom: 8px; border-bottom: 1px solid #e8e4dc; }
    td { padding: 10px 0; border-bottom: 1px solid #e8e4dc; font-size: 14px; }
    td.check { width: 24px; font-size: 16px; color: #9e9488; }
    .slip-total { margin-top: 20px; text-align: right; font-size: 14px; color: #1a1a2e; }
  </style></head><body>
  ${slipsHtml}
<script><\/script>
</body></html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
}
VIEWS['a-store-orders'] = () => {
  const sales = STORE_SALES.filter(s => s.active !== false);
  if (!sales.length) {
    return `<div class="page-head"><h2 class="serif">Merch Store <em>Orders</em></h2></div>
      <p class="empty">No active sales — check the Catalog tab if you're looking for a past one.</p>`;
  }
  if (!soSaleId || !sales.some(s => s.id === soSaleId)) soSaleId = sales[0].id;
  if (!soDataLoadedFor(soTab) && soLoadingKey !== soSaleId + ':' + soFetchTabFor(soTab)) soLoad(soSaleId, soTab);

  const current = sales.find(s => s.id === soSaleId);
  const tabBtn = (tab, label) => `<button class="btn-ghost btn-sm" style="${soTab === tab ? 'border-color:rgba(201,168,76,0.5);color:var(--gold-light)' : ''}" data-action="so-select-tab" data-tab="${tab}">${label}</button>`;

  let body = '<p class="empty">Loading…</p>';
  if (soTab === 'orders' && soOrders !== null) {
    body = !soOrders.length ? '<p class="empty">No orders placed yet.</p>' : `
      <div class="tbl-wrap"><table>
        <thead><tr><th>Date</th><th>Family</th><th>Items</th><th style="text-align:right">Total</th></tr></thead>
        <tbody>${soOrders.map(o => `<tr>
          <td style="white-space:nowrap;color:var(--muted)">${fmtLocalDT(o.createdAt)}</td>
          <td>
            <button class="tbl-link" onclick="viewParent('${o.accountId}')">${esc(o.parentName)}</button>
            <div style="font-size:11px;color:var(--muted)">${esc(o.parentEmail)}</div>
          </td>
          <td>${o.items.map(i => `<div>${esc(i.productName)} (${esc(i.size)}) x${i.quantity} — ${esc(i.recipient || '—')}</div>`).join('')}</td>
          <td style="text-align:right;white-space:nowrap">${fmt$(o.totalAmount)}</td>
        </tr>`).join('')}</tbody>
      </table></div>`;
  } else if (soTab === 'tracking' && soTracking !== null) {
    body = !soTracking.categories.length ? '<p class="empty">This sale has no required items to track.</p>' : `
      <div class="tbl-wrap"><table>
        <thead><tr><th>Performer</th><th>Parent</th>${soTracking.categories.map(c => `<th>${esc(REQUIRED_CATEGORY_LABELS[c] || c)}</th>`).join('')}</tr></thead>
        <tbody>${soTracking.performers.map(p => `<tr>
          <td><button class="tbl-link" onclick="viewParent('${p.accountId}')">${esc(p.performerName)}</button></td>
          <td style="color:var(--muted)">${esc(p.parentName)}<br>${esc(p.parentEmail)}</td>
          ${soTracking.categories.map(c => {
            const st = p.statuses[c];
            const pillClass = st === 'ordered' ? 'pill-paid' : st === 'attested' ? 'pill-amber' : 'pill-due';
            const label = st === 'ordered' ? 'Ordered' : st === 'attested' ? 'Already owns' : 'Missing';
            return `<td><span class="pill ${pillClass}">${label}</span></td>`;
          }).join('')}
        </tr>`).join('')}</tbody>
      </table></div>`;
  } else if (soTab === 'picking' && soPickingList !== null) {
    body = !soPickingList.length ? '<p class="empty">No orders placed yet.</p>' : `
      <div style="margin-bottom:14px"><button class="btn-ghost btn-sm" data-action="so-copy-fulfillment" data-sale-name="${esc(current ? current.name : '')}">Copy as text</button></div>
      <div class="tbl-wrap"><table>
        <thead><tr><th>Product</th><th>Size</th><th>Quantity</th></tr></thead>
        <tbody>${soPickingList.map(r => `<tr><td>${esc(r.productName)}</td><td>${esc(r.size)}</td><td>${r.totalQty}</td></tr>`).join('')}</tbody>
      </table></div>`;
  } else if (soTab === 'packing' && soOrders !== null) {
    const families = soPackingSlips();
    body = !families.length ? '<p class="empty">No orders placed yet.</p>' : `
      <div style="margin-bottom:14px"><button class="btn-ghost btn-sm" data-action="so-print-packing" data-sale="${soSaleId}">Print packing slips</button></div>
      ${families.map((f, i) => `
        <div style="padding:12px 0;${i < families.length - 1 ? 'border-bottom:1px solid var(--border-soft)' : ''}">
          <div style="font-size:14px;color:var(--cream);margin-bottom:6px">${esc(f.parentName)} <span style="color:var(--muted);font-size:12px">· ${fmt$(f.total)}</span></div>
          ${f.lines.map(l => `<div style="font-size:13px;color:var(--cream)">${esc(l.productName)} (${esc(l.size)}) x${l.quantity} — ${esc(l.recipient)}</div>`).join('')}
        </div>`).join('')}`;
  }

  return `
    <div class="page-head"><h2 class="serif">Merch Store <em>Orders</em></h2></div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px">
      ${sales.map(s => `<button class="btn-ghost btn-sm" style="${s.id === soSaleId ? 'border-color:rgba(201,168,76,0.5);color:var(--gold-light)' : ''}" data-action="so-select-sale" data-sale="${s.id}">${esc(s.name)}</button>`).join('')}
    </div>
    <div class="card">
      <div class="card-hd">${esc(current ? current.name : '')}
        <div style="display:flex;gap:8px;flex-wrap:wrap">${tabBtn('orders', 'Orders')}${tabBtn('tracking', 'Required Items')}${tabBtn('picking', 'Fulfillment')}${tabBtn('packing', 'Packing Slips')}<button class="btn-ghost btn-sm" data-action="so-preview-sale" data-sale="${soSaleId}">Preview</button></div>
      </div>
      ${body}
    </div>`;
};
VIEW_INIT['a-store-orders'] = wireStoreOrders;
